// A row of cards that swipes (PLAN-2026-10-17 §5, B-405): the week's games
// on a phone's League page, a recap read as a story. Mantine's Carousel on
// Embla underneath, which no screen imports (LOOKS.md Part 7).
//
// SWIPING IS NEVER THE ONLY WAY (UX-3). Under the cards, the previous and
// next arrows — an arrow alone means the next in a sequence, which this is
// (UX-41) — a finger's size on a touch screen, and dots that say where you
// are; a screen reader hears "2 of 6". The dots are pictures, not targets:
// Mantine's own are buttons a few pixels across. A drag under a mouse, and
// the arrow keys once the row has focus, move it too.

import { useState, type ReactNode } from 'react';
import { Carousel } from '@mantine/carousel';
import '@mantine/carousel/styles.css';
import type { EmblaCarouselType } from 'embla-carousel-react';
import { IconButton } from './ui';

export interface CardItem {
  key: string;
  content: ReactNode;
}

export function CardRow({
  label,
  items,
  slideSize = '100%',
  what = 'card',
}: {
  /** What the row is, for a screen reader: "This week's games". */
  label: string;
  items: CardItem[];
  /** How much of the row a card takes: under 100% shows the next one's edge. */
  slideSize?: string;
  /** What one card is, for the arrows' names: "game", "page". */
  what?: string;
}) {
  const [embla, setEmbla] = useState<EmblaCarouselType | null>(null);
  const [at, setAt] = useState(0);
  // The places it can stop: a card narrower than the row ends a little early.
  const stops = Math.max(1, embla?.scrollSnapList().length ?? items.length);
  return (
    <section className="fsp-cards" aria-roledescription="carousel" aria-label={label}>
      <Carousel
        getEmblaApi={setEmbla}
        onSlideChange={setAt}
        slideSize={slideSize}
        // A card the row's whole width needs no gap, and with one each step
        // would land a gap short of the next.
        slideGap={slideSize === '100%' ? 0 : 'sm'}
        align="start"
        containScroll="trimSnaps"
        withControls={false}
        withIndicators={false}
        classNames={{ viewport: 'fsp-cards-viewport' }}
      >
        {items.map((item, i) => (
          <Carousel.Slide
            key={item.key}
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${items.length}`}
          >
            {item.content}
          </Carousel.Slide>
        ))}
      </Carousel>
      {stops > 1 ? (
        <div className="fsp-cards-foot">
          <IconButton
            icon="previous"
            label={`Previous ${what}`}
            variant="quiet"
            disabled={at === 0}
            onClick={() => embla?.scrollPrev()}
          />
          <span className="fsp-cards-dots" aria-hidden>
            {Array.from({ length: stops }, (_, i) => (
              <span key={i} className="fsp-cards-dot" data-on={i === at || undefined} />
            ))}
          </span>
          <span className="fsp-sr" aria-live="polite">
            {`${at + 1} of ${stops}`}
          </span>
          <IconButton
            icon="next"
            label={`Next ${what}`}
            variant="quiet"
            disabled={at >= stops - 1}
            onClick={() => embla?.scrollNext()}
          />
        </div>
      ) : null}
    </section>
  );
}
